'use client'

import React, { createContext, useContext } from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { Attention, Check, Info } from '@/icons'
import { cn } from '@/utils'

export type NotificationSize = 'md' | 'lg'

interface NotificationContextType {
  size: NotificationSize
}

const NotificationContext = createContext<NotificationContextType>({
  size: 'md',
})

export type NotificationProps = {
  /**
   * Notification body. Compose it with `NotificationContent`,
   * `NotificationTitle`, `NotificationDescription` and `NotificationAction`.
   */
  children: ReactNode
  /**
   * Rendered icon element, replacing the default one picked from `variant`
   * (`Check` for `success`, `Attention` for `error`, `Info` otherwise). Pass an
   * element, not a component — e.g. `icon={<Help />}`.
   */
  icon?: ReactNode
  /**
   * Icon and type scale: `md` 24px icon, `lg` 32px icon with a larger title.
   * Read by the sub-components through context. Defaults to `md`.
   */
  size?: NotificationSize
  /**
   * Vertically centers the icon against the content instead of aligning it to
   * the first line. Use it for single-line notifications. Defaults to `false`.
   */
  centered?: boolean
} & VariantProps<typeof notificationVariants> &
  React.ComponentProps<'div'>

const notificationVariants = cva(
  'flex rounded-lg border p-3xs gap-3xs w-full',
  {
    variants: {
      variant: {
        general: 'bg-gray-200 border-gray-600 text-gray-500',
        info: 'bg-support-100 border-support-400 text-support-400',
        success: 'bg-positive-100 border-positive-400 text-positive-400',
        error: 'bg-danger-100 border-danger-400 text-danger-400',
        attention: 'bg-attention-100 border-attention-400 text-attention-400',
      },
      centered: {
        true: 'items-center',
        false: '',
      },
    },
  },
)

const notificationIconVariants = cva(
  'flex flex-shrink-0 items-center justify-center rounded-full [&>svg]:text-white',
  {
    variants: {
      variant: {
        general: 'bg-gray-400',
        info: 'bg-support-200',
        success: 'bg-positive-200',
        error: 'bg-danger-200',
        attention: 'bg-attention-400',
      },
      size: {
        md: 'h-6 w-6 [&>svg]:h-3 [&>svg]:w-3',
        lg: 'h-8 w-8 [&>svg]:h-3.5 [&>svg]:w-3.5',
      },
    },
  },
)

const notificationDescriptionVariants = cva('leading-[15px]', {
  variants: {
    size: {
      md: 'text-body-3',
      lg: 'text-body-3',
    },
  },
})

const notificationActionVariants = cva(
  'font-bold leading-[18px] underline [&_a]:text-current',
  {
    variants: {
      size: {
        md: 'text-body-3',
        lg: 'text-body-2',
      },
    },
  },
)

const notificationTitleVariants = cva('font-bold leading-[18px]', {
  variants: {
    size: {
      md: 'text-body-2',
      lg: 'text-body-2',
    },
  },
})

export function Notification({
  variant = 'general',
  icon: CustomIcon,
  className,
  children,
  size = 'md',
  centered = false,
  ...props
}: NotificationProps) {
  const getDefaultIcon = () => {
    if (variant === 'success') return Check
    if (variant === 'error') return Attention
    return Info
  }

  const DefaultIcon = getDefaultIcon()

  const contextValue = React.useMemo(() => ({ size }), [size])

  return (
    <NotificationContext.Provider value={contextValue}>
      <div
        role="alert"
        aria-live="polite"
        data-testid="notification"
        className={cn(notificationVariants({ variant, centered }), className)}
        {...props}
      >
        <div
          className={notificationIconVariants({
            variant,
            size,
          })}
          data-testid="icon"
        >
          {CustomIcon || <DefaultIcon />}
        </div>
        {children}
      </div>
    </NotificationContext.Provider>
  )
}

export function NotificationContent({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col items-start gap-[4px] py-1', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function NotificationTitle({
  children,
  className,
  ...props
}: React.ComponentProps<'span'>) {
  const { size: contextSize } = useContext(NotificationContext)

  return (
    <span
      className={cn(
        notificationTitleVariants({ size: contextSize }),
        className,
      )}
      {...props}
    >
      {children}
    </span>
  )
}

export function NotificationDescription({
  children,
  className,
  ...props
}: React.ComponentProps<'p'>) {
  const { size: contextSize } = useContext(NotificationContext)

  return (
    <p
      className={cn(
        notificationDescriptionVariants({ size: contextSize }),
        className,
      )}
      {...props}
    >
      {children}
    </p>
  )
}

export function NotificationAction({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { size: contextSize } = useContext(NotificationContext)

  return (
    <div
      className={cn(
        notificationActionVariants({ size: contextSize }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
